import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  archiveConsentDocument,
  createConsentDocument,
  fetchConsentDocument,
  fetchConsentDocuments,
  updateConsentDocument,
} from '@/api/consentDocuments/consentDocumentsApi';
import type {
  ConsentDocument,
  ConsentDocumentDetail,
  ConsentDocumentWritePayload,
} from '@/api/consentDocuments/consentDocumentsApi.types';
import {
  buildConsentDocumentQueryKey,
  CONSENT_DOCUMENTS_QUERY_KEY,
} from '@/constants/queryKeys';

export function useConsentDocumentsQuery(): UseQueryResult<ConsentDocument[], Error> {
  return useQuery({
    queryKey: CONSENT_DOCUMENTS_QUERY_KEY,
    queryFn: fetchConsentDocuments,
  });
}

export function useConsentDocumentQuery(
  documentId: string,
  isEnabled: boolean
): UseQueryResult<ConsentDocumentDetail, Error> {
  return useQuery({
    queryKey: buildConsentDocumentQueryKey(documentId),
    queryFn: function loadDocument() {
      return fetchConsentDocument(documentId);
    },
    enabled: isEnabled,
  });
}

export function useCreateConsentDocumentMutation(): UseMutationResult<
  ConsentDocument,
  Error,
  ConsentDocumentWritePayload
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createConsentDocument,
    onSuccess: function done() {
      queryClient.invalidateQueries({ queryKey: CONSENT_DOCUMENTS_QUERY_KEY });
    },
  });
}

export interface UpdateConsentDocumentVariables {
  documentId: string;
  payload: ConsentDocumentWritePayload;
}

export function useUpdateConsentDocumentMutation(): UseMutationResult<
  ConsentDocument,
  Error,
  UpdateConsentDocumentVariables
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: function update(variables: UpdateConsentDocumentVariables) {
      return updateConsentDocument(variables.documentId, variables.payload);
    },
    onSuccess: function done(document) {
      queryClient.invalidateQueries({ queryKey: CONSENT_DOCUMENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: buildConsentDocumentQueryKey(document.id) });
    },
  });
}

export function useArchiveConsentDocumentMutation(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: archiveConsentDocument,
    onSuccess: function done() {
      queryClient.invalidateQueries({ queryKey: CONSENT_DOCUMENTS_QUERY_KEY });
    },
  });
}
